import type { FC } from "react"
import React, { useEffect, useState } from "react"
import { isAddress } from "@ethersproject/address"
import { Text, Link, Stack, Flex, Icon, Box, HStack } from "@chakra-ui/react"
import Linkify from "react-linkify"

import UserAvatar from "common/components/UserAvatar"
import type { OnchainVote, Vote } from "query/graphql"
import VoteLegend from "voting/components/VoteLegend"
import CircleWavesIcon from "ui/components/icons/CircleWavesIcon"
import { ROUTES } from "common/constants/routes"
import { getVoteStyles } from "common/helpers/voteStyle"
import {
  getDateFromNow,
  getDateFromTimestamp,
  getTimestampFromIetf,
} from "common/helpers/date"
import { shortString } from "common/helpers/string"
import { getDisplayName } from "user/helpers/user"
import { fetcher } from "common/helpers/fetcher"
import DiscourseIcon from "ui/components/icons/DiscourseIcon"
import { useDevice } from "common/hooks/useDevice"
import { DATE_FORMATS } from "common/constants/date"

type Props = {
  slug: string
  votes: Vote[]
}

const ProposalComments: FC<Props> = ({ votes, slug }) => {
  const hasComments = votes.length > 0

  const byOnchainAndNonEmptyReason = (vote: Vote): vote is OnchainVote =>
    "chainId" in vote && Boolean(vote.reason)

  const voteStyles = getVoteStyles()
  const { onLittleDevice, onLargeDevice } = useDevice()

  return (
    <Stack spacing={{ base: 3, lg: 0 }}>
      {hasComments ? (
        votes
          .filter(byOnchainAndNonEmptyReason)
          .map(({ voter, block, reason, type }) => {
            const { timestamp: ietfTimestamp } = block

            const timestamp = getTimestampFromIetf(ietfTimestamp)
            const date = getDateFromTimestamp(timestamp)
            const dateLabel = date.format(DATE_FORMATS.monthDayYear)

            const dateLabelAgo = getDateFromNow(ietfTimestamp.toString())

            const { picture, address, twitter } = voter
            const displayName = getDisplayName(voter)
            const twitterUsername = twitter

            const hasTwitterUsername = Boolean(twitterUsername)

            const endpoint = ROUTES.governance.delegate.profile(slug, address)

            // Check for Discourse links in the reason text
            const discoursePattern = /https?:\/\/[\w.-]+\/t\/[\w-]+\/\d+\/(\d+)/
            const discourseMatch = reason?.match(discoursePattern)
            const discourseLink = discourseMatch?.[0]
            const postNumber = discourseMatch
              ? parseInt(discourseMatch[1], 10)
              : null

            return (
              <Stack
                key={`proposal_comment_${address}`}
                _notLast={{
                  borderBottom: "gray.light",
                }}
                align="stretch"
                border={{ base: "gray.light", lg: "none" }}
                p={{ base: 0, lg: 10 }}
                spacing={{ base: 0, lg: 6 }}
              >
                <Flex
                  align={{ base: "stretch", lg: "center" }}
                  justify="space-between"
                  p={{ base: 2, lg: 0 }}
                  w="full"
                >
                  <Link
                    isExternal
                    _hover={{ textDecoration: "none" }}
                    href={endpoint}
                  >
                    <HStack spacing={2} w="full">
                      <UserAvatar
                        address={address}
                        isVerifiedByTwitter={hasTwitterUsername}
                        size={8}
                        src={picture}
                      />

                      <Text color="gray.900" textStyle="body.regular.md">
                        {isAddress(displayName)
                          ? shortString(displayName)
                          : displayName}
                      </Text>

                      <Text
                        color="gray.500"
                        display={onLargeDevice}
                        textStyle="body.regular.md"
                      >
                        {!isAddress(displayName)
                          ? `(${shortString(address, 3, -3)})`
                          : null}
                      </Text>
                    </HStack>
                  </Link>
                  <Link
                    isExternal
                    _hover={{ textDecoration: "none" }}
                    href={endpoint}
                  >
                    <Stack
                      align="center"
                      direction={{ base: "row-reverse", lg: "row" }}
                      pb={{ base: 3, lg: 0 }}
                      px={{ base: 0, lg: 0 }}
                      spacing={2}
                    >
                      <Text
                        color="gray.600"
                        display={onLargeDevice}
                        textStyle="body.regular.md"
                        whiteSpace="nowrap"
                      >
                        {`Voted ${dateLabel}`}
                      </Text>
                      <Text
                        color="gray.600"
                        display={onLittleDevice}
                        textStyle="body.regular.md"
                        whiteSpace="nowrap"
                      >
                        {`${dateLabelAgo} ago`}
                      </Text>
                      <VoteLegend type={type} voteStyle={voteStyles[type]} />
                    </Stack>
                  </Link>
                </Flex>
                <Flex
                  direction="column"
                  pb={{ base: 3, lg: 0 }}
                  px={{ base: 3, lg: 0 }}
                >
                  <Linkify
                    componentDecorator={(
                      href: string | undefined,
                      text: string,
                      key: React.Key | null | undefined,
                    ) => (
                      <Link
                        key={key}
                        isExternal
                        color="purple.500"
                        href={href}
                        textDecoration="underline"
                      >
                        {text}
                      </Link>
                    )}
                  >
                    <Text
                      color="gray.600"
                      textStyle="body.regular.md"
                      width="100%"
                    >
                      {reason}
                    </Text>
                  </Linkify>
                  {discourseLink && postNumber && (
                    <DiscourseCommentComponent
                      postNumber={postNumber}
                      url={discourseLink}
                    />
                  )}
                </Flex>
              </Stack>
            )
          })
      ) : (
        <Flex
          align="center"
          direction="column"
          h="24rem"
          justify="center"
          w="full"
        >
          <Icon as={CircleWavesIcon} h={20} mb={4} w={20} />
          <Text color="gray.600" textStyle="body.bold.lg">
            No comments yet
          </Text>
          <Text color="gray.600" textStyle="body.regular.md">
            New comments will appear here
          </Text>
        </Flex>
      )}
    </Stack>
  )
}

type CommentData = {
  title: string
  content: string
  forumUrl: string
}

type DiscourseCommentComponentProps = {
  url: string
  postNumber: number
}

const DiscourseCommentComponent: React.FC<DiscourseCommentComponentProps> = ({
  url,
  postNumber,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [commentDetails, setCommentDetails] = useState<{
    title: string
    content: string
    link: string
    forumUrl: string
  } | null>(null)

  useEffect(() => {
    const fetchCommentData = async () => {
      setIsLoading(true)
      try {
        const data = await fetcher.rest<CommentData>({
          endpoint: "/api/comment/import",
          method: "POST",
          variables: { url, postNumber },
        })

        if (data) {
          const forumUrlMatch = url.match(
            /(https?:\/\/[\w.-]+\/t\/[\w-]+\/\d+)\/\d+/,
          )
          const forumUrl = forumUrlMatch ? forumUrlMatch[1] : ""

          setCommentDetails({
            title: data.title,
            content: data.content,
            link: url,
            forumUrl,
          })
        }
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    }

    fetchCommentData()
  }, [url, postNumber])

  if (isLoading) {
    return (
      <Text p={5} textStyle="body.regular.md">
        Loading...
      </Text>
    )
  }

  if (!commentDetails) return null

  const truncatedContent =
    commentDetails.content.length > 400
      ? `${commentDetails.content.slice(0, 400)}...`
      : commentDetails.content

  return (
    <Box borderLeftColor="gray.200" borderLeftWidth="4px" mt={4} p={3}>
      <HStack align="top">
        <Icon as={DiscourseIcon} mt={0.5} />
        <Link isExternal href={commentDetails.forumUrl}>
          <Text color="purple.500" textStyle="body.bold.md">
            {commentDetails.title}
          </Text>
        </Link>
      </HStack>
      <Text
        mb={1}
        mt={2}
        overflow="hidden"
        textOverflow="ellipsis"
        textStyle="body.regular.md"
      >
        {truncatedContent}
      </Text>
      {commentDetails.content.length > 400 && (
        <Link
          color="purple.500"
          href={commentDetails.link}
          rel="noopener noreferrer"
          target="_blank"
        >
          Read more
        </Link>
      )}
    </Box>
  )
}

export default ProposalComments
